import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import type { EditLike } from '../earlierVersions.ts';
import type { SentTo } from '../tasks/model.ts';

/**
 * The communications log (138): a client's feed, the unmatched list, and the writes. Every write is a POST body;
 * the feed's only query key is the fixed "withdrawn" switch (no free text in a URL).
 */

export const CHANNELS = [
  ['phone', 'Phone'], ['email', 'Email'], ['letter', 'Letter'], ['fax', 'Fax'], ['in_person', 'In person'], ['video', 'Video'],
] as const;
export const DIRECTIONS = [['inbound', 'Inbound'], ['outbound', 'Outbound'], ['none', 'Neither']] as const;
export const OUTCOMES = [
  ['informational', 'Informational'], ['action_required', 'Action required'], ['incident', 'Incident'], ['follow_up', 'Follow-up'],
] as const;
export const DEPARTMENTS = ['Social Work', 'Finance', 'Legal', 'Property', 'Compliance', 'Executive', 'AP/AR'] as const;
/** The feed's department buttons (Arthur, 20 October, 139), then All. */
export const FEED_DEPARTMENTS = ['Finance', 'Social Work', 'Legal', 'Compliance'] as const;
/** The kinds the "Other" menu logs; a call and an email have buttons of their own. */
export const OTHER_CHANNELS = CHANNELS.filter(([k]) => k !== 'phone' && k !== 'email');

export type Channel = (typeof CHANNELS)[number][0];
export type Direction = (typeof DIRECTIONS)[number][0];
export type Outcome = (typeof OUTCOMES)[number][0];
export type Department = (typeof DEPARTMENTS)[number];

export interface Participant { kind: 'to' | 'cc' | 'bcc'; name?: string | null; address: string }

export const labelOf = (list: readonly (readonly [string, string])[], v: string | null) => list.find(([k]) => k === v)?.[1] ?? v ?? '';

export interface Communication {
  id: string;
  client_id: string | null;
  client_ref: string | null;
  occurred_at: string;
  duration_minutes: number | null;
  channel: Channel;
  direction: Direction;
  contact_id: string | null;
  who: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  department: Department;
  summary: string | null;
  outcome: Outcome;
  is_emergency: boolean;
  subject: string | null;
  participants: Participant[];
  body: string | null;
  follow_up_task_id: string | null;
  follow_up_status: string | null;
  follow_up_due: string | null;
  source: string;
  recorded_at: string;
  recorded_by: string | null;
  current_version: number;
  withdrawn: boolean;
  withdrawal_reason: string | null;
  may_edit: boolean;
}

export interface CommunicationFacts {
  occurred_at: string;
  duration_minutes: number | null;
  channel: Channel;
  direction: Direction;
  contact_id: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  /** null: the logger's own, which the database fills in; tiers 1-2 always choose */
  department: Department | null;
  summary: string | null;
  outcome: Outcome;
  is_emergency: boolean;
  subject: string | null;
  participants: Participant[];
  /** exactly as pasted (139): never trimmed */
  body: string | null;
}

/** The reader's department as the forms and the feed use it (139). */
export interface DepartmentView {
  /** the department an entry they log takes; null for tiers 1-2 */
  own_department: Department | null;
  picks_department: boolean;
  default_feed_department: string;
}

export interface Feed extends DepartmentView {
  entries: Communication[];
  contacts: { id: string; name: string; role: string | null; phone: string | null; email: string | null }[];
  staff: { id: string; full_name: string }[];
  primary: { department: string; id: string }[];
  may_log: boolean;
  open_emergencies: number;
}

export interface CommunicationVersion {
  version: number;
  kind: 'as recorded' | 'edit' | 'withdrawal';
  reason: string | null;
  reason_label: string | null;
  reason_text: string | null;
  recorded_by: string | null;
  recorded_at: string;
  changes: { field: string; label: string; before: string | null; after: string | null }[];
}

function post<T>(getToken: () => Promise<string>, path: string, body: unknown) {
  return apiFetch<T>(path, getToken, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
}

export function useClientCommunications(clientId: string, withdrawn: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'communications', withdrawn],
    queryFn: () => apiFetch<Feed>(`/clients/${clientId}/communications${withdrawn ? '?withdrawn=1' : ''}`, getToken),
    ...queryDefaults,
  });
}

export function useUnmatchedCommunications() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['communications', 'unmatched'],
    queryFn: () => apiFetch<{ entries: Communication[]; may_log: boolean; sees_all_unmatched: boolean } & DepartmentView>('/communications/unmatched', getToken),
    ...queryDefaults,
  });
}

export function useCommunicationVersions(id: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['communication', id, 'versions'],
    queryFn: () => apiFetch<{ versions: CommunicationVersion[] }>(`/communications/${id}/versions`, getToken),
    ...queryDefaults,
    enabled,
  });
}

/** After any write, the feeds, the unmatched list, a version list and the client's tasks read again. */
function useChanged() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['communications'] });
    void qc.invalidateQueries({ queryKey: ['communication'] });
    void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'client' && (q.queryKey[2] === 'communications' || q.queryKey[2] === 'tasks') });
  };
}

export function useLogCommunication(clientId: string | null) {
  const { getToken } = useSession();
  const changed = useChanged();
  return useMutation({
    mutationFn: (f: CommunicationFacts) => post<{ entry: Communication }>(getToken, clientId ? `/clients/${clientId}/communications` : '/communications', f),
    onSuccess: changed,
  });
}

export function useEditCommunication(id: string) {
  const { getToken } = useSession();
  const changed = useChanged();
  return useMutation({
    mutationFn: (b: CommunicationFacts & { reason: string; reason_text: string | null }) =>
      post<{ entry: Communication; version: number }>(getToken, `/communications/${id}/edit`, b),
    onSuccess: changed,
  });
}

export function useWithdrawCommunication(id: string) {
  const { getToken } = useSession();
  const changed = useChanged();
  return useMutation({
    mutationFn: (reason: string) => post<{ entry: Communication }>(getToken, `/communications/${id}/withdraw`, { reason }),
    onSuccess: changed,
  });
}

export function useMatchCommunication(id: string) {
  const { getToken } = useSession();
  const changed = useChanged();
  return useMutation({
    mutationFn: (client_ref: string) => post<{ entry: Communication }>(getToken, `/communications/${id}/match`, { client_ref }),
    onSuccess: changed,
  });
}

export function useFollowUp(id: string) {
  const { getToken } = useSession();
  const changed = useChanged();
  return useMutation({
    mutationFn: (b: { title: string; detail: string | null; assigned_to: string; due_date: string }) =>
      post<{ task_id: string; sent_to: SentTo }>(getToken, `/communications/${id}/follow-up`, b),
    onSuccess: changed,
  });
}

/** The versions as the shared EarlierVersions component reads them (as visit notes, 125). */
export function asEdits(versions: CommunicationVersion[]): EditLike[] {
  return versions.map((v) => ({ version: v.version, kind: v.kind, by: v.recorded_by, at: v.recorded_at,
    reason: v.reason_label ?? v.reason, reason_text: v.reason_text, changes: v.changes }));
}
